import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  Film,
  BarChart2,
  Settings,
  Bell,
  LogOut,
  Plus,
  Eye,
  Edit2,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Star,
  TrendingUp,
  Users,
  Play,
  ChevronRight,
  X,
  Globe,
  Calendar,
  Tag,
  FileText,
  Image,
  Video,
  AlertCircle,
  Menu,
  Home,
  DollarSign,
  BookOpen,
} from "lucide-react";

// ── Mock Data ──────────────────────────────────────────────
const MOCK_CREATOR = {
  name: "Kofi Asante Films",
  country: "Ghana",
  region: "West Africa",
  avatar: "KA",
  status: "verified",
  joinedYear: 2024,
  totalViews: 142800,
  totalFilms: 12,
  totalRevenue: 3240,
  rating: 4.7,
};

const MOCK_FILMS = [
  {
    id: 1,
    title: "Abyssinia Rising",
    year: 2019,
    country: "Ethiopia",
    genre: "Historical Drama",
    status: "published",
    views: 48200,
    rating: 4.8,
    duration: "1h 52m",
    quality: "4K",
    thumbnail:
      "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400&h=220&fit=crop",
    uploadDate: "2024-03-12",
  },
  {
    id: 2,
    title: "Kente Threads",
    year: 2021,
    country: "Ghana",
    genre: "Documentary",
    status: "published",
    views: 31700,
    rating: 4.6,
    duration: "58m",
    quality: "2K",
    thumbnail:
      "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=400&h=220&fit=crop",
    uploadDate: "2024-04-01",
  },
  {
    id: 3,
    title: "Sahel Voices",
    year: 2022,
    country: "Mali",
    genre: "Documentary",
    status: "under_review",
    views: 0,
    rating: null,
    duration: "1h 10m",
    quality: "4K",
    thumbnail:
      "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&h=220&fit=crop",
    uploadDate: "2024-06-08",
  },
  {
    id: 4,
    title: "Lagos After Midnight",
    year: 2023,
    country: "Nigeria",
    genre: "Drama",
    status: "draft",
    views: 0,
    rating: null,
    duration: "2h 4m",
    quality: "4K",
    thumbnail:
      "https://images.unsplash.com/photo-1485846234645-a62644f84728?w=400&h=220&fit=crop",
    uploadDate: "2024-06-09",
  },
];

const MOCK_NOTIFICATIONS = [
  {
    id: 1,
    type: "approved",
    text: "Abyssinia Rising has been approved and is now live.",
    time: "2h ago",
    read: false,
  },
  {
    id: 2,
    type: "review",
    text: "Sahel Voices is under editorial review. Expected 24hrs.",
    time: "5h ago",
    read: false,
  },
  {
    id: 3,
    type: "milestone",
    text: "Kente Threads hit 30,000 views! 🎉",
    time: "1d ago",
    read: true,
  },
  {
    id: 4,
    type: "comment",
    text: "New review on Abyssinia Rising: 5 stars.",
    time: "2d ago",
    read: true,
  },
];

const GENRES = [
  "Historical Drama",
  "Documentary",
  "Short Film",
  "Series",
  "Animation",
  "Avant-Garde",
  "Social Satire",
  "Epic/Mythology",
  "Romance",
  "War/Historical",
  "Cultural Heritage",
];
const LANGUAGES = [
  "Amharic",
  "Arabic",
  "English",
  "French",
  "Hausa",
  "Igbo",
  "Kiswahili",
  "Oromo",
  "Portuguese",
  "Somali",
  "Twi",
  "Wolof",
  "Xhosa",
  "Yoruba",
  "Zulu",
  "Other",
];
const QUALITIES = ["HD (720p)", "Full HD (1080p)", "2K", "4K"];

// ── Status Badge ───────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    published: {
      label: "Published",
      icon: <CheckCircle size={12} />,
      color: "#22c55e",
      bg: "rgba(34,197,94,0.12)",
    },
    under_review: {
      label: "Under Review",
      icon: <Clock size={12} />,
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.12)",
    },
    draft: {
      label: "Draft",
      icon: <Edit2 size={12} />,
      color: "#94a3b8",
      bg: "rgba(148,163,184,0.12)",
    },
    rejected: {
      label: "Rejected",
      icon: <XCircle size={12} />,
      color: "#ef4444",
      bg: "rgba(239,68,68,0.12)",
    },
  };
  const s = map[status] || map.draft;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 999,
        background: s.bg,
        color: s.color,
        fontSize: "0.73rem",
        fontWeight: 600,
      }}
    >
      {s.icon}
      {s.label}
    </span>
  );
};

// ── Stat Card ──────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, accent }) => (
  <div style={css.statCard}>
    <div style={{ ...css.statIcon, background: accent + "22", color: accent }}>
      {icon}
    </div>
    <div>
      <div style={css.statValue}>{value}</div>
      <div style={css.statLabel}>{label}</div>
      {sub && <div style={css.statSub}>{sub}</div>}
    </div>
  </div>
);

// ── Upload Modal ───────────────────────────────────────────
const UploadModal = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const videoRef = useRef();
  const posterRef = useRef();

  const [meta, setMeta] = useState({
    title: "",
    year: "",
    country: "",
    language: "",
    genre: "",
    quality: "",
    duration: "",
    description: "",
    significance: "",
    tags: "",
    ageRating: "PG",
  });

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("video/")) setVideoFile(file);
  };

  const simulateUpload = () => {
    setUploading(true);
    let p = 0;
    const iv = setInterval(() => {
      p += Math.random() * 12;
      if (p >= 100) {
        p = 100;
        clearInterval(iv);
        setUploading(false);
        setStep(3);
      }
      setUploadProgress(Math.min(p, 100));
    }, 300);
  };

  const handleMeta = (e) =>
    setMeta((m) => ({ ...m, [e.target.name]: e.target.value }));

  return (
    <div style={css.modalOverlay} onClick={onClose}>
      <div style={css.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={css.modalHeader}>
          <div>
            <div style={css.modalTitle}>Upload Film</div>
            <div style={css.modalSub}>
              Step {step} of 3 —{" "}
              {["Select File", "Film Details", "Done"][step - 1]}
            </div>
          </div>
          <button style={css.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Step Pills */}
        <div style={css.stepRow}>
          {["File", "Details", "Done"].map((s, i) => (
            <React.Fragment key={s}>
              <div
                style={{
                  ...css.stepPill,
                  ...(step > i
                    ? css.stepPillDone
                    : step === i + 1
                      ? css.stepPillActive
                      : {}),
                }}
              >
                {step > i + 1 ? <CheckCircle size={14} /> : i + 1} {s}
              </div>
              {i < 2 && (
                <div
                  style={{
                    ...css.stepConnector,
                    ...(step > i + 1 ? css.stepConnectorDone : {}),
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: File Upload */}
        {step === 1 && (
          <div>
            <div
              style={{
                ...css.dropZone,
                ...(dragging ? css.dropZoneActive : {}),
                ...(videoFile ? css.dropZoneDone : {}),
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => !videoFile && videoRef.current.click()}
            >
              <input
                ref={videoRef}
                type="file"
                accept="video/*"
                style={{ display: "none" }}
                onChange={(e) => setVideoFile(e.target.files[0])}
              />
              {videoFile ? (
                <div style={css.filePreview}>
                  <Video size={32} color="#e8b94f" />
                  <div style={css.fileName}>{videoFile.name}</div>
                  <div style={css.fileSize}>
                    {(videoFile.size / 1024 / 1024).toFixed(1)} MB
                  </div>
                  <button
                    style={css.removeFile}
                    onClick={(e) => {
                      e.stopPropagation();
                      setVideoFile(null);
                    }}
                  >
                    <X size={14} /> Remove
                  </button>
                </div>
              ) : (
                <div style={css.dropContent}>
                  <div style={css.dropIcon}>
                    <Upload size={28} />
                  </div>
                  <div style={css.dropTitle}>Drop your film file here</div>
                  <div style={css.dropSub}>MP4, MOV, MKV · up to 50GB</div>
                  <button style={css.dropBtn}>Browse Files</button>
                </div>
              )}
            </div>

            {/* Poster */}
            <div style={css.posterRow}>
              <div style={css.posterLabel}>
                <Image size={16} color="#e8b94f" /> Poster / Thumbnail
              </div>
              <button
                style={css.posterBtn}
                onClick={() => posterRef.current.click()}
              >
                <input
                  ref={posterRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => setPosterFile(e.target.files[0])}
                />
                {posterFile ? (
                  <>
                    <CheckCircle size={14} color="#22c55e" /> {posterFile.name}
                  </>
                ) : (
                  <>
                    <Plus size={14} /> Upload Poster
                  </>
                )}
              </button>
            </div>

            <button
              style={{
                ...css.primaryBtn,
                marginTop: 16,
                opacity: videoFile ? 1 : 0.4,
              }}
              disabled={!videoFile}
              onClick={() => setStep(2)}
            >
              Continue to Film Details <ChevronRight size={18} />
            </button>
          </div>
        )}

        {/* Step 2: Metadata */}
        {step === 2 && (
          <div style={css.metaForm}>
            <div style={css.formRow}>
              <div style={css.formGroup}>
                <label style={css.formLabel}>Film Title *</label>
                <input
                  name="title"
                  style={css.input}
                  placeholder="e.g. Abyssinia Rising"
                  value={meta.title}
                  onChange={handleMeta}
                />
              </div>
              <div style={css.formGroup}>
                <label style={css.formLabel}>Release Year *</label>
                <input
                  name="year"
                  style={css.input}
                  placeholder="e.g. 1975"
                  value={meta.year}
                  onChange={handleMeta}
                />
              </div>
            </div>
            <div style={css.formRow}>
              <div style={css.formGroup}>
                <label style={css.formLabel}>Country of Origin *</label>
                <input
                  name="country"
                  style={css.input}
                  placeholder="e.g. Ethiopia"
                  value={meta.country}
                  onChange={handleMeta}
                />
              </div>
              <div style={css.formGroup}>
                <label style={css.formLabel}>Duration *</label>
                <input
                  name="duration"
                  style={css.input}
                  placeholder="e.g. 1h 42m"
                  value={meta.duration}
                  onChange={handleMeta}
                />
              </div>
            </div>
            <div style={css.formRow}>
              <div style={css.formGroup}>
                <label style={css.formLabel}>Genre *</label>
                <select
                  name="genre"
                  style={css.input}
                  value={meta.genre}
                  onChange={handleMeta}
                >
                  <option value="">Select genre</option>
                  {GENRES.map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </select>
              </div>
              <div style={css.formGroup}>
                <label style={css.formLabel}>Language *</label>
                <select
                  name="language"
                  style={css.input}
                  value={meta.language}
                  onChange={handleMeta}
                >
                  <option value="">Select language</option>
                  {LANGUAGES.map((l) => (
                    <option key={l}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={css.formRow}>
              <div style={css.formGroup}>
                <label style={css.formLabel}>Video Quality *</label>
                <select
                  name="quality"
                  style={css.input}
                  value={meta.quality}
                  onChange={handleMeta}
                >
                  <option value="">Select quality</option>
                  {QUALITIES.map((q) => (
                    <option key={q}>{q}</option>
                  ))}
                </select>
              </div>
              <div style={css.formGroup}>
                <label style={css.formLabel}>Age Rating</label>
                <select
                  name="ageRating"
                  style={css.input}
                  value={meta.ageRating}
                  onChange={handleMeta}
                >
                  {["G", "PG", "PG-13", "R", "NR"].map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={css.formGroup}>
              <label style={css.formLabel}>Description *</label>
              <textarea
                name="description"
                style={{ ...css.input, height: 90, resize: "none" }}
                placeholder="Tell viewers what this film is about..."
                value={meta.description}
                onChange={handleMeta}
              />
            </div>
            <div style={css.formGroup}>
              <label style={css.formLabel}>
                Cultural / Historical Significance
              </label>
              <textarea
                name="significance"
                style={{ ...css.input, height: 70, resize: "none" }}
                placeholder="Why does this film matter to African heritage cinema?"
                value={meta.significance}
                onChange={handleMeta}
              />
            </div>
            <div style={css.formGroup}>
              <label style={css.formLabel}>Tags (comma separated)</label>
              <input
                name="tags"
                style={css.input}
                placeholder="e.g. independence, Nollywood, 1970s"
                value={meta.tags}
                onChange={handleMeta}
              />
            </div>

            <div style={css.rightsBox}>
              <AlertCircle size={15} color="#e8b94f" />
              <span>
                By uploading, you confirm you own or are licensed to distribute
                this content on Reel Ancestry.
              </span>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button style={css.ghostBtn} onClick={() => setStep(1)}>
                Back
              </button>
              {uploading ? (
                <div style={css.progressWrap}>
                  <div style={css.progressBar}>
                    <div
                      style={{
                        ...css.progressFill,
                        width: uploadProgress + "%",
                      }}
                    />
                  </div>
                  <span style={css.progressLabel}>
                    Uploading… {Math.round(uploadProgress)}%
                  </span>
                </div>
              ) : (
                <button
                  style={{ ...css.primaryBtn, flex: 1 }}
                  onClick={simulateUpload}
                  disabled={!meta.title || !meta.year || !meta.genre}
                >
                  <Upload size={17} /> Upload & Submit for Review
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Done */}
        {step === 3 && (
          <div style={css.doneScreen}>
            <div style={css.doneIcon}>🎬</div>
            <div style={css.doneTitle}>Submitted for Review</div>
            <div style={css.doneSub}>
              <strong style={{ color: "#e8b94f" }}>
                {meta.title || "Your film"}
              </strong>{" "}
              has been uploaded and is now in our editorial queue. You'll
              receive a notification within 48 hours.
            </div>
            <div style={css.doneSteps}>
              {[
                "Upload complete",
                "Editorial review (24–48h)",
                "Published on Reel Ancestry",
              ].map((s, i) => (
                <div key={s} style={css.doneStep}>
                  <div
                    style={{
                      ...css.doneStepDot,
                      background:
                        i === 0
                          ? "#22c55e"
                          : i === 1
                            ? "#e8b94f"
                            : "rgba(255,255,255,0.15)",
                    }}
                  />
                  <span
                    style={{
                      color:
                        i === 0
                          ? "#22c55e"
                          : i === 1
                            ? "#e8b94f"
                            : "rgba(255,255,255,0.4)",
                    }}
                  >
                    {s}
                  </span>
                </div>
              ))}
            </div>
            <button style={css.primaryBtn} onClick={onClose}>
              Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Dashboard ─────────────────────────────────────────
const CreatorDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [showUpload, setShowUpload] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const unread = notifications.filter((n) => !n.read).length;

  const navItems = [
    { id: "overview", icon: <Home size={18} />, label: "Overview" },
    { id: "films", icon: <Film size={18} />, label: "My Films" },
    { id: "analytics", icon: <BarChart2 size={18} />, label: "Analytics" },
    { id: "earnings", icon: <DollarSign size={18} />, label: "Earnings" },
    { id: "guidelines", icon: <BookOpen size={18} />, label: "Guidelines" },
    { id: "settings", icon: <Settings size={18} />, label: "Settings" },
  ];

  const markAllRead = () =>
    setNotifications((n) => n.map((x) => ({ ...x, read: true })));

  return (
    <div style={css.root}>
      {/* Sidebar */}
      <aside
        style={{ ...css.sidebar, ...(sidebarOpen ? css.sidebarOpen : {}) }}
      >
        <div style={css.sidebarTop}>
          <div style={css.brandMark}>
            🎬 <span style={css.brandText}>Reel Ancestry</span>
          </div>
          <div style={css.creatorBadge}>Creator Studio</div>
        </div>

        {/* Avatar */}
        <div style={css.avatarBlock}>
          <div style={css.avatar}>{MOCK_CREATOR.avatar}</div>
          <div>
            <div style={css.creatorName}>{MOCK_CREATOR.name}</div>
            <div style={css.creatorMeta}>
              {MOCK_CREATOR.country} · {MOCK_CREATOR.region}
            </div>
            <div style={css.verifiedBadge}>
              <CheckCircle size={11} /> Verified Creator
            </div>
          </div>
        </div>

        <nav style={css.nav}>
          {navItems.map((item) => (
            <button
              key={item.id}
              style={{
                ...css.navItem,
                ...(activeTab === item.id ? css.navItemActive : {}),
              }}
              onClick={() => {
                setActiveTab(item.id);
                setSidebarOpen(false);
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <button style={css.navItem} onClick={() => navigate("/")}>
          <LogOut size={18} /> <span>Exit Studio</span>
        </button>
      </aside>

      {/* Main */}
      <main style={css.main}>
        {/* Top Bar */}
        <div style={css.topbar}>
          <button
            style={css.menuBtn}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu size={22} />
          </button>
          <div style={css.topbarTitle}>
            {navItems.find((n) => n.id === activeTab)?.label}
          </div>
          <div style={css.topbarRight}>
            <div style={{ position: "relative" }}>
              <button
                style={css.iconBtn}
                onClick={() => setShowNotifs(!showNotifs)}
              >
                <Bell size={20} />
                {unread > 0 && <span style={css.notifDot}>{unread}</span>}
              </button>
              {showNotifs && (
                <div style={css.notifPanel}>
                  <div style={css.notifHeader}>
                    <span>Notifications</span>
                    <button style={css.markRead} onClick={markAllRead}>
                      Mark all read
                    </button>
                  </div>
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      style={{
                        ...css.notifItem,
                        ...(n.read ? {} : css.notifUnread),
                      }}
                    >
                      <div style={css.notifText}>{n.text}</div>
                      <div style={css.notifTime}>{n.time}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button style={css.uploadBtn} onClick={() => setShowUpload(true)}>
              <Plus size={16} /> Upload Film
            </button>
          </div>
        </div>

        {/* ── Overview ── */}
        {activeTab === "overview" && (
          <div style={css.content}>
            <div style={css.welcomeBar}>
              <div>
                <div style={css.welcomeTitle}>
                  Good day, {MOCK_CREATOR.name.split(" ")[0]} 👋
                </div>
                <div style={css.welcomeSub}>
                  Here's how your films are performing this week.
                </div>
              </div>
              <button
                style={css.primaryBtn}
                onClick={() => setShowUpload(true)}
              >
                <Upload size={16} /> Upload New Film
              </button>
            </div>

            <div style={css.statsGrid}>
              <StatCard
                icon={<Eye size={20} />}
                label="Total Views"
                value={MOCK_CREATOR.totalViews.toLocaleString()}
                sub="↑ 12% this month"
                accent="#e8b94f"
              />
              <StatCard
                icon={<Film size={20} />}
                label="Films Published"
                value={MOCK_CREATOR.totalFilms}
                sub="2 in review"
                accent="#c084fc"
              />
              <StatCard
                icon={<Star size={20} />}
                label="Avg. Rating"
                value={MOCK_CREATOR.rating}
                sub="Across all films"
                accent="#f87171"
              />
              <StatCard
                icon={<DollarSign size={20} />}
                label="Total Earnings"
                value={`$${MOCK_CREATOR.totalRevenue.toLocaleString()}`}
                sub="Last payout: Jun 1"
                accent="#34d399"
              />
            </div>

            {/* Recent Films */}
            <div style={css.sectionHeader}>
              <div style={css.sectionTitle}>Recent Films</div>
              <button style={css.seeAll} onClick={() => setActiveTab("films")}>
                See all <ChevronRight size={14} />
              </button>
            </div>
            <div style={css.filmList}>
              {MOCK_FILMS.slice(0, 3).map((film) => (
                <FilmRow key={film.id} film={film} />
              ))}
            </div>

            {/* Quick Actions */}
            <div style={css.sectionHeader}>
              <div style={css.sectionTitle}>Quick Actions</div>
            </div>
            <div style={css.quickGrid}>
              {[
                {
                  icon: <Upload size={22} />,
                  label: "Upload Film",
                  action: () => setShowUpload(true),
                  color: "#e8b94f",
                },
                {
                  icon: <BarChart2 size={22} />,
                  label: "View Analytics",
                  action: () => setActiveTab("analytics"),
                  color: "#c084fc",
                },
                {
                  icon: <BookOpen size={22} />,
                  label: "Content Guidelines",
                  action: () => setActiveTab("guidelines"),
                  color: "#34d399",
                },
                {
                  icon: <Settings size={22} />,
                  label: "Edit Profile",
                  action: () => setActiveTab("settings"),
                  color: "#f87171",
                },
              ].map((q) => (
                <button key={q.label} style={css.quickCard} onClick={q.action}>
                  <div
                    style={{
                      ...css.quickIcon,
                      color: q.color,
                      background: q.color + "22",
                    }}
                  >
                    {q.icon}
                  </div>
                  <div style={css.quickLabel}>{q.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── My Films ── */}
        {activeTab === "films" && (
          <div style={css.content}>
            <div style={css.welcomeBar}>
              <div>
                <div style={css.welcomeTitle}>My Films</div>
                <div style={css.welcomeSub}>
                  {MOCK_FILMS.length} films ·{" "}
                  {MOCK_FILMS.filter((f) => f.status === "published").length}{" "}
                  published
                </div>
              </div>
              <button
                style={css.primaryBtn}
                onClick={() => setShowUpload(true)}
              >
                <Plus size={16} /> Upload Film
              </button>
            </div>

            {/* Filter Tabs */}
            <div style={css.filterRow}>
              {["all", "published", "under_review", "draft"].map((f) => (
                <button key={f} style={css.filterChip}>
                  {f === "all"
                    ? "All"
                    : f === "under_review"
                      ? "Under Review"
                      : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            <div style={css.filmGrid}>
              {MOCK_FILMS.map((film) => (
                <div key={film.id} style={css.filmCard}>
                  <div style={css.filmThumbWrap}>
                    <img
                      src={film.thumbnail}
                      alt={film.title}
                      style={css.filmThumb}
                    />
                    <div style={css.filmOverlay}>
                      <button style={css.filmPlayBtn}>
                        <Play size={18} fill="white" />
                      </button>
                    </div>
                    <span style={css.qualityBadge}>{film.quality}</span>
                  </div>
                  <div style={css.filmCardBody}>
                    <div style={css.filmCardTitle}>{film.title}</div>
                    <div style={css.filmCardMeta}>
                      {film.year} · {film.country} · {film.duration}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 8,
                      }}
                    >
                      <StatusBadge status={film.status} />
                      {film.status === "published" && (
                        <span style={css.viewCount}>
                          <Eye size={12} /> {film.views.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div style={css.filmActions}>
                      <button style={css.actionBtn}>
                        <Edit2 size={14} /> Edit
                      </button>
                      <button style={{ ...css.actionBtn, color: "#ef4444" }}>
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Upload New Card */}
              <button
                style={css.uploadCard}
                onClick={() => setShowUpload(true)}
              >
                <div style={css.uploadCardIcon}>
                  <Plus size={28} />
                </div>
                <div style={css.uploadCardLabel}>Upload New Film</div>
              </button>
            </div>
          </div>
        )}

        {/* ── Analytics ── */}
        {activeTab === "analytics" && (
          <div style={css.content}>
            <div style={css.welcomeTitle} style={{ marginBottom: 20 }}>
              Analytics
            </div>
            <div style={css.statsGrid}>
              <StatCard
                icon={<Eye size={20} />}
                label="Views This Month"
                value="18,400"
                sub="↑ 22% vs last month"
                accent="#e8b94f"
              />
              <StatCard
                icon={<Users size={20} />}
                label="Unique Viewers"
                value="9,210"
                sub="↑ 8% vs last month"
                accent="#c084fc"
              />
              <StatCard
                icon={<TrendingUp size={20} />}
                label="Watch Time"
                value="4,200h"
                sub="Avg. 14min per session"
                accent="#34d399"
              />
              <StatCard
                icon={<Star size={20} />}
                label="New Reviews"
                value="47"
                sub="Avg. 4.7 stars"
                accent="#f87171"
              />
            </div>

            <div style={css.sectionHeader}>
              <div style={css.sectionTitle}>Views by Film</div>
            </div>
            <div style={css.analyticsTable}>
              <div style={css.tableHeader}>
                {["Film", "Status", "Views", "Rating", "Upload Date"].map(
                  (h) => (
                    <div key={h} style={css.th}>
                      {h}
                    </div>
                  ),
                )}
              </div>
              {MOCK_FILMS.map((f, i) => (
                <div
                  key={f.id}
                  style={{
                    ...css.tableRow,
                    ...(i % 2 === 0 ? css.tableRowAlt : {}),
                  }}
                >
                  <div style={{ ...css.td, fontWeight: 600, color: "#fff" }}>
                    {f.title}
                  </div>
                  <div style={css.td}>
                    <StatusBadge status={f.status} />
                  </div>
                  <div style={css.td}>
                    {f.views > 0 ? f.views.toLocaleString() : "—"}
                  </div>
                  <div style={css.td}>
                    {f.rating ? (
                      <>
                        <Star size={12} style={{ color: "#e8b94f" }} />{" "}
                        {f.rating}
                      </>
                    ) : (
                      "—"
                    )}
                  </div>
                  <div style={css.td}>{f.uploadDate}</div>
                </div>
              ))}
            </div>

            {/* Fake bar chart */}
            <div style={css.sectionHeader}>
              <div style={css.sectionTitle}>Monthly Views (2024)</div>
            </div>
            <div style={css.chartWrap}>
              {[12, 28, 19, 34, 48, 31, 41, 52, 38, 44, 29, 18].map((v, i) => (
                <div key={i} style={css.barCol}>
                  <div style={{ ...css.bar, height: (v / 52) * 180 + "px" }} />
                  <div style={css.barLabel}>
                    {
                      [
                        "J",
                        "F",
                        "M",
                        "A",
                        "M",
                        "J",
                        "J",
                        "A",
                        "S",
                        "O",
                        "N",
                        "D",
                      ][i]
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Earnings ── */}
        {activeTab === "earnings" && (
          <div style={css.content}>
            <div style={css.welcomeTitle}>Earnings</div>
            <div style={{ ...css.statsGrid, marginTop: 20 }}>
              <StatCard
                icon={<DollarSign size={20} />}
                label="Total Earned"
                value="$3,240"
                sub="Since joining"
                accent="#e8b94f"
              />
              <StatCard
                icon={<DollarSign size={20} />}
                label="This Month"
                value="$480"
                sub="Paid on Jul 1"
                accent="#34d399"
              />
              <StatCard
                icon={<DollarSign size={20} />}
                label="Pending"
                value="$120"
                sub="Next payout Jul 15"
                accent="#c084fc"
              />
              <StatCard
                icon={<Eye size={20} />}
                label="Monetized Views"
                value="89,200"
                sub="At $0.036 CPM"
                accent="#f87171"
              />
            </div>
            <div style={css.earningsNote}>
              <AlertCircle size={15} color="#e8b94f" />
              <span>
                Revenue is calculated monthly from ad impressions and
                subscription shares. Payouts via PayPal or bank transfer on the
                1st of each month.
              </span>
            </div>
            <div style={css.sectionHeader}>
              <div style={css.sectionTitle}>Payout History</div>
            </div>
            <div style={css.analyticsTable}>
              <div style={css.tableHeader}>
                {["Month", "Views", "Revenue", "Status"].map((h) => (
                  <div key={h} style={css.th}>
                    {h}
                  </div>
                ))}
              </div>
              {[
                { m: "June 2024", v: "18,400", r: "$480", s: "Paid" },
                { m: "May 2024", v: "21,300", r: "$552", s: "Paid" },
                { m: "April 2024", v: "16,800", r: "$436", s: "Paid" },
                { m: "March 2024", v: "14,200", r: "$368", s: "Paid" },
              ].map((row, i) => (
                <div
                  key={i}
                  style={{
                    ...css.tableRow,
                    ...(i % 2 === 0 ? css.tableRowAlt : {}),
                  }}
                >
                  <div style={{ ...css.td, color: "#fff", fontWeight: 600 }}>
                    {row.m}
                  </div>
                  <div style={css.td}>{row.v}</div>
                  <div style={{ ...css.td, color: "#34d399", fontWeight: 700 }}>
                    {row.r}
                  </div>
                  <div style={css.td}>
                    <span
                      style={{
                        color: "#22c55e",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                      }}
                    >
                      <CheckCircle size={12} /> {row.s}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Guidelines ── */}
        {activeTab === "guidelines" && (
          <div style={css.content}>
            <div style={css.welcomeTitle}>Content Guidelines</div>
            <div style={css.guideSub}>
              Everything you need to know to publish on Reel Ancestry.
            </div>
            {[
              {
                icon: "🎬",
                title: "African Heritage Focus",
                body: "All content must relate to African cinema, culture, history, or diaspora stories. This includes restored classics, new films by African filmmakers, documentaries on African heritage, and series rooted in African narratives.",
              },
              {
                icon: "⚖️",
                title: "Rights & Licensing",
                body: "You must own full distribution rights or hold valid licensing for every film you upload. Uploading content you do not own rights to will result in immediate removal and possible account suspension.",
              },
              {
                icon: "✅",
                title: "Editorial Review",
                body: "Every film goes through our editorial team before publishing. Review takes 24–48 hours. Films may be returned for improved metadata, better poster images, or context on cultural significance.",
              },
              {
                icon: "🖼️",
                title: "Quality Standards",
                body: "Videos must be at minimum 720p HD. Posters must be at least 600×900px. Audio must be clean and synced. Subtitles in English are strongly encouraged for non-English language films.",
              },
              {
                icon: "💰",
                title: "Monetization",
                body: "Verified creators earn a share of ad revenue and subscription fees based on watch time. Revenue is calculated monthly and paid on the 1st. Minimum payout threshold is $50.",
              },
              {
                icon: "🚫",
                title: "Prohibited Content",
                body: "Content that promotes violence, hate speech, exploitation, or that is otherwise illegal is strictly prohibited and will be removed immediately.",
              },
            ].map((g) => (
              <div key={g.title} style={css.guideCard}>
                <div style={css.guideIcon}>{g.icon}</div>
                <div>
                  <div style={css.guideTitle}>{g.title}</div>
                  <div style={css.guideBody}>{g.body}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Settings ── */}
        {activeTab === "settings" && (
          <div style={css.content}>
            <div style={css.welcomeTitle}>Profile & Settings</div>
            <div style={css.settingsGrid}>
              {/* Profile */}
              <div style={css.settingsCard}>
                <div style={css.settingsCardTitle}>
                  <User size={16} /> Creator Profile
                </div>
                <div style={css.formGroup}>
                  <label style={css.formLabel}>Creator / Studio Name</label>
                  <input style={css.input} defaultValue={MOCK_CREATOR.name} />
                </div>
                <div style={css.formGroup}>
                  <label style={css.formLabel}>Country</label>
                  <input
                    style={css.input}
                    defaultValue={MOCK_CREATOR.country}
                  />
                </div>
                <div style={css.formGroup}>
                  <label style={css.formLabel}>African Region</label>
                  <select style={css.input} defaultValue={MOCK_CREATOR.region}>
                    {[
                      "West Africa",
                      "East Africa",
                      "North Africa",
                      "Central Africa",
                      "Southern Africa",
                      "African Diaspora",
                    ].map((r) => (
                      <option key={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div style={css.formGroup}>
                  <label style={css.formLabel}>Bio</label>
                  <textarea
                    style={{ ...css.input, height: 80, resize: "none" }}
                    defaultValue="Award-winning Ghanaian filmmaker specialising in restored West African classics and new cultural documentaries."
                  />
                </div>
                <div style={css.formGroup}>
                  <label style={css.formLabel}>Website</label>
                  <input
                    style={css.input}
                    defaultValue="https://kofiasantefilms.com"
                  />
                </div>
                <button style={css.primaryBtn}>Save Profile</button>
              </div>

              {/* Payout */}
              <div style={css.settingsCard}>
                <div style={css.settingsCardTitle}>
                  <DollarSign size={16} /> Payout Settings
                </div>
                <div style={css.formGroup}>
                  <label style={css.formLabel}>Payout Method</label>
                  <select style={css.input}>
                    <option>PayPal</option>
                    <option>Bank Transfer</option>
                    <option>Mobile Money</option>
                  </select>
                </div>
                <div style={css.formGroup}>
                  <label style={css.formLabel}>PayPal Email</label>
                  <input style={css.input} placeholder="paypal@email.com" />
                </div>
                <button style={css.primaryBtn}>Save Payout Info</button>

                <div style={{ ...css.settingsCardTitle, marginTop: 24 }}>
                  <Bell size={16} /> Notifications
                </div>
                {[
                  "Email me when a film is approved",
                  "Email me on new reviews",
                  "Monthly earnings summary",
                ].map((n) => (
                  <label
                    key={n}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      marginBottom: 12,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      defaultChecked
                      style={{ accentColor: "#e8b94f" }}
                    />
                    <span
                      style={{
                        color: "rgba(255,255,255,0.75)",
                        fontSize: "0.85rem",
                      }}
                    >
                      {n}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} />}
      {sidebarOpen && (
        <div style={css.overlay} onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

// ── Film Row (overview) ────────────────────────────────────
const FilmRow = ({ film }) => (
  <div style={css.filmRow}>
    <img src={film.thumbnail} alt={film.title} style={css.rowThumb} />
    <div style={{ flex: 1 }}>
      <div style={css.rowTitle}>{film.title}</div>
      <div style={css.rowMeta}>
        {film.year} · {film.country} · {film.genre}
      </div>
    </div>
    <StatusBadge status={film.status} />
    {film.status === "published" && (
      <div style={css.rowViews}>
        <Eye size={13} /> {film.views.toLocaleString()}
      </div>
    )}
  </div>
);

// ── Styles ─────────────────────────────────────────────────
const GOLD = "#e8b94f";
const GOLD_DIM = "#c8963e";
const BG = "#0d0a04";
const SURFACE = "#1a1208";
const SURFACE2 = "#231a0d";
const BORDER = "rgba(232,185,79,0.12)";
const TEXT = "rgba(255,255,255,0.88)";
const TEXT_DIM = "rgba(255,255,255,0.45)";

const css = {
  root: {
    display: "flex",
    minHeight: "100vh",
    background: BG,
    fontFamily: "'Inter',system-ui,sans-serif",
    color: TEXT,
  },
  sidebar: {
    width: 240,
    background: SURFACE,
    borderRight: `1px solid ${BORDER}`,
    display: "flex",
    flexDirection: "column",
    padding: "20px 0",
    flexShrink: 0,
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    zIndex: 100,
    transition: "transform 0.3s ease",
    overflowY: "auto",
    "@media(max-width:768px)": { transform: "translateX(-100%)" },
  },
  sidebarOpen: { transform: "translateX(0)" },
  sidebarTop: {
    padding: "0 20px 20px",
    borderBottom: `1px solid ${BORDER}`,
    marginBottom: 16,
  },
  brandMark: {
    fontSize: "1.1rem",
    fontWeight: 800,
    color: GOLD,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  brandText: { letterSpacing: "0.02em" },
  creatorBadge: {
    marginTop: 4,
    fontSize: "0.68rem",
    color: TEXT_DIM,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  avatarBlock: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 20px 16px",
    borderBottom: `1px solid ${BORDER}`,
    marginBottom: 8,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    background: `linear-gradient(135deg,${GOLD_DIM},${GOLD})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "1rem",
    color: "#1a0a00",
    flexShrink: 0,
  },
  creatorName: { fontWeight: 700, fontSize: "0.85rem", color: "#fff" },
  creatorMeta: { fontSize: "0.72rem", color: TEXT_DIM, marginTop: 2 },
  verifiedBadge: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.68rem",
    color: "#22c55e",
    marginTop: 4,
  },
  nav: { flex: 1, padding: "0 10px" },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    background: "transparent",
    border: "none",
    color: TEXT_DIM,
    fontSize: "0.875rem",
    cursor: "pointer",
    textAlign: "left",
    transition: "all 0.2s",
    marginBottom: 2,
  },
  navItemActive: {
    background: `linear-gradient(135deg,${GOLD_DIM}22,${GOLD}11)`,
    color: GOLD,
    borderLeft: `2px solid ${GOLD}`,
  },
  main: {
    flex: 1,
    marginLeft: 240,
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },
  topbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 24px",
    background: SURFACE,
    borderBottom: `1px solid ${BORDER}`,
    position: "sticky",
    top: 0,
    zIndex: 50,
  },
  menuBtn: {
    background: "transparent",
    border: "none",
    color: TEXT_DIM,
    cursor: "pointer",
    display: "none",
    padding: 4,
  },
  topbarTitle: { flex: 1, fontWeight: 700, fontSize: "1.05rem", color: "#fff" },
  topbarRight: { display: "flex", alignItems: "center", gap: 10 },
  iconBtn: {
    background: "transparent",
    border: "none",
    color: TEXT_DIM,
    cursor: "pointer",
    padding: 6,
    borderRadius: 8,
    position: "relative",
    transition: "color 0.2s",
  },
  notifDot: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: GOLD,
    color: "#1a0a00",
    fontSize: "0.6rem",
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  notifPanel: {
    position: "absolute",
    top: 40,
    right: 0,
    width: 300,
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
    zIndex: 200,
  },
  notifHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: `1px solid ${BORDER}`,
    fontWeight: 700,
    fontSize: "0.85rem",
  },
  markRead: {
    background: "transparent",
    border: "none",
    color: GOLD,
    fontSize: "0.75rem",
    cursor: "pointer",
  },
  notifItem: { padding: "12px 16px", borderBottom: `1px solid ${BORDER}` },
  notifUnread: { background: "rgba(232,185,79,0.06)" },
  notifText: { fontSize: "0.8rem", color: TEXT, lineHeight: 1.5 },
  notifTime: { fontSize: "0.7rem", color: TEXT_DIM, marginTop: 4 },
  uploadBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: `linear-gradient(135deg,${GOLD_DIM},${GOLD})`,
    color: "#1a0a00",
    border: "none",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: "0.8rem",
    cursor: "pointer",
  },
  content: { padding: "24px", maxWidth: 1100, width: "100%" },
  welcomeBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  welcomeTitle: {
    fontSize: "1.4rem",
    fontWeight: 800,
    color: "#fff",
    marginBottom: 4,
  },
  welcomeSub: { fontSize: "0.875rem", color: TEXT_DIM },
  primaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    background: `linear-gradient(135deg,${GOLD_DIM},${GOLD})`,
    color: "#1a0a00",
    border: "none",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: "0.875rem",
    cursor: "pointer",
  },
  ghostBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 16px",
    background: "transparent",
    color: TEXT_DIM,
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    fontWeight: 600,
    fontSize: "0.875rem",
    cursor: "pointer",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
    gap: 14,
    marginBottom: 28,
  },
  statCard: {
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    padding: "16px 18px",
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  statIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 800,
    color: "#fff",
    lineHeight: 1,
  },
  statLabel: { fontSize: "0.78rem", color: TEXT_DIM, marginTop: 4 },
  statSub: { fontSize: "0.72rem", color: "rgba(34,197,94,0.9)", marginTop: 3 },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    marginTop: 8,
  },
  sectionTitle: { fontWeight: 700, fontSize: "1rem", color: "#fff" },
  seeAll: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    background: "transparent",
    border: "none",
    color: GOLD,
    fontSize: "0.8rem",
    cursor: "pointer",
    fontWeight: 600,
  },
  filmList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 24,
  },
  filmRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: "10px 14px",
  },
  rowThumb: {
    width: 72,
    height: 44,
    borderRadius: 8,
    objectFit: "cover",
    flexShrink: 0,
  },
  rowTitle: { fontWeight: 600, fontSize: "0.875rem", color: "#fff" },
  rowMeta: { fontSize: "0.75rem", color: TEXT_DIM, marginTop: 3 },
  rowViews: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.78rem",
    color: TEXT_DIM,
    flexShrink: 0,
  },
  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))",
    gap: 12,
  },
  quickCard: {
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    padding: "18px 14px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    transition: "border-color 0.2s",
  },
  quickIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  quickLabel: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: TEXT,
    textAlign: "center",
  },
  filmGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
    gap: 16,
  },
  filmCard: {
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    overflow: "hidden",
  },
  filmThumbWrap: { position: "relative" },
  filmThumb: {
    width: "100%",
    height: 120,
    objectFit: "cover",
    display: "block",
  },
  filmOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    transition: "opacity 0.2s",
  },
  filmPlayBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: GOLD,
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  qualityBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    background: "rgba(0,0,0,0.75)",
    color: GOLD,
    fontSize: "0.65rem",
    fontWeight: 700,
    padding: "2px 7px",
    borderRadius: 6,
  },
  filmCardBody: { padding: "12px" },
  filmCardTitle: {
    fontWeight: 700,
    fontSize: "0.875rem",
    color: "#fff",
    marginBottom: 4,
  },
  filmCardMeta: { fontSize: "0.72rem", color: TEXT_DIM },
  viewCount: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    color: TEXT_DIM,
  },
  filmActions: { display: "flex", gap: 8, marginTop: 10 },
  actionBtn: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    padding: "6px",
    background: "rgba(255,255,255,0.05)",
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    color: TEXT_DIM,
    fontSize: "0.75rem",
    cursor: "pointer",
  },
  uploadCard: {
    background: "transparent",
    border: `2px dashed rgba(232,185,79,0.25)`,
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: "pointer",
    minHeight: 200,
    transition: "border-color 0.2s",
  },
  uploadCardIcon: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    background: "rgba(232,185,79,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: GOLD,
  },
  uploadCardLabel: { fontSize: "0.85rem", fontWeight: 600, color: TEXT_DIM },
  filterRow: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  filterChip: {
    padding: "6px 14px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.05)",
    border: `1px solid ${BORDER}`,
    color: TEXT_DIM,
    fontSize: "0.78rem",
    cursor: "pointer",
  },
  analyticsTable: {
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    overflow: "hidden",
    marginBottom: 24,
  },
  tableHeader: {
    display: "grid",
    gridTemplateColumns: "2fr 1.2fr 1fr 0.8fr 1fr",
    background: "rgba(232,185,79,0.08)",
    padding: "10px 16px",
    borderBottom: `1px solid ${BORDER}`,
  },
  tableRow: {
    display: "grid",
    gridTemplateColumns: "2fr 1.2fr 1fr 0.8fr 1fr",
    padding: "12px 16px",
    borderBottom: `1px solid ${BORDER}`,
    alignItems: "center",
  },
  tableRowAlt: { background: "rgba(255,255,255,0.02)" },
  th: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: TEXT_DIM,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  td: {
    fontSize: "0.82rem",
    color: TEXT_DIM,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  chartWrap: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    padding: "16px",
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    height: 220,
    marginBottom: 24,
  },
  barCol: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    height: "100%",
  },
  bar: {
    width: "100%",
    background: `linear-gradient(180deg,${GOLD},${GOLD_DIM})`,
    borderRadius: "4px 4px 0 0",
    minHeight: 4,
    alignSelf: "flex-end",
    opacity: 0.85,
  },
  barLabel: { fontSize: "0.65rem", color: TEXT_DIM },
  earningsNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    background: "rgba(232,185,79,0.08)",
    border: `1px solid rgba(232,185,79,0.2)`,
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.7)",
    marginTop: 16,
    marginBottom: 16,
    lineHeight: 1.6,
  },
  guideCard: {
    display: "flex",
    gap: 16,
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    padding: "18px",
    marginBottom: 12,
  },
  guideIcon: { fontSize: "1.8rem", flexShrink: 0, lineHeight: 1 },
  guideTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#fff",
    marginBottom: 6,
  },
  guideBody: { fontSize: "0.83rem", color: TEXT_DIM, lineHeight: 1.65 },
  guideSub: { color: TEXT_DIM, fontSize: "0.875rem", marginBottom: 20 },
  settingsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
    gap: 16,
    marginTop: 20,
  },
  settingsCard: {
    background: SURFACE2,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    padding: "20px",
  },
  settingsCardTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: GOLD,
    marginBottom: 16,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  formGroup: { marginBottom: 14 },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 0,
  },
  formLabel: {
    display: "block",
    fontSize: "0.77rem",
    fontWeight: 600,
    color: TEXT_DIM,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    background: "rgba(255,255,255,0.06)",
    border: `1px solid rgba(255,255,255,0.12)`,
    borderRadius: 10,
    color: TEXT,
    fontSize: "0.875rem",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  // Upload Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    zIndex: 300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modal: {
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 20,
    width: "100%",
    maxWidth: 560,
    maxHeight: "90vh",
    overflowY: "auto",
    padding: 24,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  modalTitle: { fontWeight: 800, fontSize: "1.2rem", color: "#fff" },
  modalSub: { fontSize: "0.8rem", color: TEXT_DIM, marginTop: 4 },
  closeBtn: {
    background: "transparent",
    border: "none",
    color: TEXT_DIM,
    cursor: "pointer",
    padding: 4,
  },
  stepRow: { display: "flex", alignItems: "center", marginBottom: 24 },
  stepPill: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 12px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.06)",
    color: TEXT_DIM,
    fontSize: "0.78rem",
    fontWeight: 600,
    border: `1px solid rgba(255,255,255,0.1)`,
  },
  stepPillActive: {
    background: "rgba(232,185,79,0.15)",
    color: GOLD,
    border: `1px solid ${GOLD}33`,
  },
  stepPillDone: {
    background: "rgba(34,197,94,0.15)",
    color: "#22c55e",
    border: "1px solid rgba(34,197,94,0.3)",
  },
  stepConnector: {
    flex: 1,
    height: 2,
    background: "rgba(255,255,255,0.08)",
    margin: "0 6px",
  },
  stepConnectorDone: { background: "rgba(34,197,94,0.4)" },
  dropZone: {
    border: `2px dashed rgba(232,185,79,0.25)`,
    borderRadius: 16,
    padding: "32px 24px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s",
    marginBottom: 16,
  },
  dropZoneActive: { borderColor: GOLD, background: "rgba(232,185,79,0.06)" },
  dropZoneDone: {
    borderColor: "#22c55e",
    background: "rgba(34,197,94,0.04)",
    cursor: "default",
  },
  dropContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },
  dropIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    background: "rgba(232,185,79,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: GOLD,
  },
  dropTitle: { fontWeight: 700, fontSize: "1rem", color: "#fff" },
  dropSub: { fontSize: "0.8rem", color: TEXT_DIM },
  dropBtn: {
    marginTop: 4,
    padding: "8px 20px",
    background: `linear-gradient(135deg,${GOLD_DIM},${GOLD})`,
    color: "#1a0a00",
    border: "none",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: "0.8rem",
    cursor: "pointer",
  },
  filePreview: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  },
  fileName: { fontWeight: 600, fontSize: "0.875rem", color: "#fff" },
  fileSize: { fontSize: "0.75rem", color: TEXT_DIM },
  removeFile: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    background: "rgba(239,68,68,0.1)",
    border: "1px solid rgba(239,68,68,0.2)",
    color: "#ef4444",
    borderRadius: 8,
    padding: "5px 12px",
    fontSize: "0.75rem",
    cursor: "pointer",
  },
  posterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 10,
    padding: "10px 14px",
    marginBottom: 4,
  },
  posterLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.83rem",
    fontWeight: 600,
    color: TEXT_DIM,
  },
  posterBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    background: "rgba(232,185,79,0.1)",
    border: `1px solid rgba(232,185,79,0.2)`,
    borderRadius: 8,
    color: GOLD,
    fontSize: "0.78rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  metaForm: { display: "flex", flexDirection: "column", gap: 0 },
  rightsBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    background: "rgba(232,185,79,0.08)",
    border: `1px solid rgba(232,185,79,0.2)`,
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.65)",
    lineHeight: 1.6,
    marginTop: 8,
  },
  progressWrap: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 6,
  },
  progressBar: {
    height: 8,
    background: "rgba(255,255,255,0.1)",
    borderRadius: 999,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: `linear-gradient(90deg,${GOLD_DIM},${GOLD})`,
    borderRadius: 999,
    transition: "width 0.3s",
  },
  progressLabel: { fontSize: "0.78rem", color: TEXT_DIM },
  doneScreen: { textAlign: "center", padding: "16px 0" },
  doneIcon: { fontSize: "3rem", marginBottom: 12 },
  doneTitle: {
    fontWeight: 800,
    fontSize: "1.3rem",
    color: "#fff",
    marginBottom: 10,
  },
  doneSub: {
    fontSize: "0.875rem",
    color: TEXT_DIM,
    lineHeight: 1.7,
    marginBottom: 24,
  },
  doneSteps: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "flex-start",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 24,
    textAlign: "left",
  },
  doneStep: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: "0.83rem",
  },
  doneStepDot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    zIndex: 99,
  },
};

export default CreatorDashboard;
